import { Link } from '@tanstack/react-router'
import { Lock } from 'lucide-react'
import { useTranslation } from '@kostbar/i18n'
import type { BaseUnitGuardReason } from '@/api/endpoints/product-stock'
import { formatDate } from '@/shared/utils/format-date'
import { formatShare, formatStockQuantity } from '@/shared/lib/stock-quantity'

/**
 * Why an article's base unit cannot change right now (CHG-2026-09-stock-usage-and-price-twins,
 * D13): stock per location, dishes using it, variants, open deliveries — the reasons of
 * `productStockApi.baseUnitGuard`.
 *
 * Rendered by `ProductPackageUnitsSection` below the base-unit select, which stays locked while
 * `canChange` is false (task 4.6).
 */
export function BaseUnitGuardReasons({
  reasons,
}: {
  reasons: Array<BaseUnitGuardReason>
}) {
  const { t: tProducts } = useTranslation('products')
  const stock = reasons.filter((r) => r.type === 'stock')
  const dishes = reasons.filter((r) => r.type === 'used_in_dish')
  const variants = reasons.filter((r) => r.type === 'variant')
  const deliveries = reasons.filter((r) => r.type === 'open_delivery')

  return (
    <div
      role="note"
      className="space-y-2 rounded-md border border-warning/50 bg-warning/10 px-3 py-2 text-sm"
    >
      <p className="flex items-center gap-2 font-medium">
        <Lock className="h-4 w-4 shrink-0 text-warning" />
        {tProducts('packageUnits.blockedTitle')}
      </p>
      <p className="text-muted-foreground">
        {tProducts('packageUnits.blockedDescription')}
      </p>
      <ul className="space-y-1">
        {stock.length > 0 && (
          <li>
            {tProducts('packageUnits.reasonStock', {
              list: stock
                .map((r) =>
                  tProducts('packageUnits.reasonStockAt', {
                    qty: formatStockQuantity(r.availableQty),
                    restaurant: r.restaurantName ?? String(r.restaurantId),
                  }),
                )
                .join(', '),
            })}
          </li>
        )}
        {dishes.length > 0 && (
          <li>
            {tProducts('packageUnits.reasonDishes')}{' '}
            {dishes.map((dish, index) => (
              <span key={dish.sku}>
                {index > 0 && ', '}
                <Link
                  to={`/products/$sku` as '/products/$sku'}
                  params={{ sku: dish.sku }}
                  className="underline"
                >
                  {dish.name}
                </Link>{' '}
                ({formatStockQuantity(dish.qty)})
              </span>
            ))}
          </li>
        )}
        {variants.length > 0 && (
          <li>
            {tProducts('packageUnits.reasonVariants', {
              list: variants
                .map((v) => `${v.name} (${formatShare(v.share)})`)
                .join(', '),
            })}
          </li>
        )}
        {deliveries.length > 0 && (
          <li>
            {tProducts('packageUnits.reasonDeliveries', {
              list: deliveries
                .map((d) =>
                  [
                    d.restaurantName ?? String(d.restaurantId),
                    d.expectedDate ? formatDate(d.expectedDate) : null,
                  ]
                    .filter(Boolean)
                    .join(' '),
                )
                .join(', '),
            })}
          </li>
        )}
      </ul>
      <p className="text-xs text-muted-foreground">
        {tProducts('packageUnits.blockedHint')}
      </p>
    </div>
  )
}
